import React, { createContext, useState, useContext, useEffect } from 'react';
import { login as loginApi, logout as logoutApi } from '../api/authApi';
import { decodeToken, setToken as saveToken, removeToken, getToken } from '../utils/helpers';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = getToken();
    if (token) {
      try {
        const decoded = decodeToken(token);
        // JWT structure: { sub: "agent_id", email, role, name, team_lead_id }
        setUser({
          id: decoded.sub,
          email: decoded.email,
          role: decoded.role,
          name: decoded.name,
          team_lead_id: decoded.team_lead_id
        });
      } catch (error) {
        removeToken();
      }
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    try {
      const response = await loginApi(email, password);
      const { access_token, agent } = response;
      
      saveToken(access_token);
      
      const decoded = decodeToken(access_token);
      setUser({
        id: decoded.sub,
        email: decoded.email,
        role: decoded.role,
        name: decoded.name,
        team_lead_id: decoded.team_lead_id
      });
      
      return { success: true, agent };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.msg || 'Login failed'
      };
    }
  };

  const logout = async () => {
    try {
      await logoutApi();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      removeToken();
      setUser(null);
    }
  };

  const hasRole = (roles) => {
    if (!user) return false;
    if (Array.isArray(roles)) {
      return roles.includes(user.role);
    }
    return user.role === roles;
  };

  const value = {
    user,
    loading,
    login,
    logout,
    hasRole,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
